import { AppDispatch } from "@/app/store";
import TableList, { TableColumn } from "@/components/common/TableList";
import { selectEventArticles, selectEventError, selectEventLoading } from "@/features/news/newsSelectors";
import { fetchEventArticles } from "@/features/news/newsThunks";
import { EventArticle } from "@/types/news.types";
import { Alert, Chip, CircularProgress, IconButton, Tooltip, Snackbar } from "@mui/material";
import MuiAlert from '@mui/material/Alert';
import { Box } from "@mui/system";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';


interface EventsTableProps {
    onEdit?: (event: EventArticle) => void
    onDelete?: (event: EventArticle) => void
}

const EventsTable: FC<EventsTableProps> = ({ onEdit, onDelete }) => {
    const dispatch = useDispatch<AppDispatch>();
    const events = useSelector(selectEventArticles)
    const loadingEvents = useSelector(selectEventLoading);
    const errorEvents = useSelector(selectEventError);

    useEffect(() => {
        dispatch(fetchEventArticles());
    }, [dispatch])

    const [snackOpen, setSnackOpen] = useState(false)
    const [snackMsg, setSnackMsg] = useState('')
    const [snackSeverity, setSnackSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('error')
    const showSnackbar = (msg: string, sev: 'success' | 'error' | 'info' | 'warning' = 'error') => { setSnackMsg(msg); setSnackSeverity(sev); setSnackOpen(true) }
    const handleSnackClose = () => setSnackOpen(false)

    const handleEdit = async (event: EventArticle) => {
        // delegate edit action to parent if provided
        try {
            onEdit?.(event)
        } catch (err) {
            console.error('Error handling edit:', err)
            showSnackbar('Error al iniciar edición. Por favor, intente nuevamente.', 'error')
        }
    };

    const handleDelete = async (event: EventArticle) => {
        // delegate delete action to parent if provided
        try {
            onDelete?.(event)
        } catch (err) {
            console.error('Error handling delete:', err)
            showSnackbar('Error al eliminar el evento. Por favor, intente nuevamente.', 'error')
        }
    };


    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}-${month}-${year} ${hours}:${minutes}`;
    };

    const columns: TableColumn<EventArticle>[] = [
        { key: 'title', label: 'Título', width: 270, headerSx: { color: '#374151' } },
        {
            key: 'manager', label: 'Responsable', render: (e) => (
                <Chip label={e.manager} size="small" sx={{ backgroundColor: '#d1f4eb', color: '#00695c', fontWeight: 500 }} />
            ), headerSx: { color: '#374151' }
        },
        {
            key: 'date',
            label: 'Fecha',
            render: (e) => formatDate(e.date),
            headerSx: { color: '#374151' }
        },
        { key: 'place', label: 'Lugar', headerSx: { color: '#374151' } },
        { key: 'site', label: 'Sede', headerSx: { color: '#374151' } },
        {
            key: 'actions', label: 'Acciones', align: 'center', width: 100, render: (e) => (
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="Editar">
                        <IconButton
                            size="small"
                            onClick={() => handleEdit(e)}
                            sx={{ color: '#6b7280', '&:hover': { color: 'primary.main' } }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    {/* Botón de Eliminar (NUEVO) */}
                    <Tooltip title="Eliminar">
                        <IconButton
                            size="small"
                            onClick={() => handleDelete(e)}
                            sx={{ color: '#6b7280', '&:hover': { color: '#ef4444' } }} // Color de hover rojo
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
            cellSx: { px: 1 }
        }
    ];
    if (loadingEvents) {
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                <CircularProgress />
            </Box>
        )
    }
    if (errorEvents) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error">{errorEvents}</Alert>
            </Box>
        );
    }
    return (
        <Box sx={{ py: 2, px: { xs: 2, md: 3, lg: 4 }, mb: 4 }}>
            <Snackbar open={snackOpen} autoHideDuration={4000} onClose={handleSnackClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <MuiAlert onClose={handleSnackClose} severity={snackSeverity} sx={{ width: '100%' }}>{snackMsg}</MuiAlert>
            </Snackbar>
            <TableList
                data={events}
                columns={columns}
                title="Eventos"
                getRowKey={(e) => e.id}
                containerSx={{ borderRadius: 2, border: '1px solid #e5e7eb', pb: 5 }}
                tableSx={{ p: 5 }}
                headerBackground={true}
                emptyMessage="No hay eventos registrados"
            />
        </Box>
    )
}

export default EventsTable;